// OrgManageModal - super-admin org management: edit details, set billing/pricing,
// suspend/reactivate, delete, manage members, and pending invites (copy/resend).
// All actions call staff-only RPCs.

import { useState, useEffect } from 'react';
import { X, Building2, CreditCard, AlertTriangle, Trash2, Pause, Play, Users, UserMinus, Mail, Copy, Send, Check, Award, FileText, Download } from 'lucide-react';
import {
  staffUpdateOrg, staffGetBilling, staffSetBilling, staffSetOrgStatus, staffDeleteOrg,
  staffListMembers, staffSetMemberRole, staffRemoveMember,
  staffListInvitations, sendInviteEmail,
} from '../lib/adminOrgApi.js';
import { staffGetTenantPlan, staffSetTenantPlan, staffClearTenantPlan } from '../lib/adminPlanApi.js';
import { staffGenerateSeatInvoice, staffListSeatInvoices, staffMarkSeatInvoicePaid, staffVoidSeatInvoice, openInvoiceDocument } from '../lib/adminInvoiceApi.js';

const CONSOLE_URL = 'https://console.govern360.ai';
const TIERS = ['starter', 'business', 'enterprise', 'trial'];
const SIZES = ['SMB', 'Mid-market', 'Enterprise'];

export default function OrgManageModal({ org, onClose, onChanged }) {
  const [tab, setTab] = useState('details');
  const [d, setD] = useState({ name: org.name || '', industry: org.industry || '', size: org.size || 'SMB', plan: org.plan || 'trial', primary_color: org.primary_color || '', slug: org.slug || '' });
  const [b, setB] = useState({ tier: 'starter', monthly_min: '', rate_per_tau: '', token_allowance: '', overage_rate: '', contract_end: '', billing_email: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [confirmDel, setConfirmDel] = useState(false);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [copiedTok, setCopiedTok] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [resentId, setResentId] = useState(null);
  const ROLES = ['owner','admin','security','policy_author','risk_manager','viewer'];
  const [plan, setPlan] = useState(null);            // staff_get_tenant_plan result
  const [invoices, setInvoices] = useState([]);
  const [invPeriod, setInvPeriod] = useState(() => new Date().toISOString().slice(0, 7) + '-01');
  function loadInvoices() { staffListSeatInvoices(org.id).then(({ data }) => setInvoices(data || [])); }
  async function genInvoice() {
    setBusy(true); setMsg('');
    const { data, error } = await staffGenerateSeatInvoice(org.id, invPeriod, null);
    setBusy(false);
    if (error) { setMsg('Error: ' + error.message); return; }
    setMsg('Invoice generated.'); loadInvoices();
    if (data) openInvoiceDocument(data, org);
  }
  async function markPaid(inv) {
    const method = window.prompt('Payment method? (stripe / check / wire / other)', 'check');
    if (!method) return;
    const reference = window.prompt('Reference (check #, txn id) - optional', '') || null;
    setBusy(true); setMsg('');
    const { error } = await staffMarkSeatInvoicePaid(inv.id, method.trim().toLowerCase(), reference);
    setBusy(false);
    if (error) { setMsg('Error: ' + error.message); return; }
    setMsg('Marked paid.'); loadInvoices();
  }
  async function voidInvoice(inv) {
    setBusy(true); setMsg('');
    const { error } = await staffVoidSeatInvoice(inv.id);
    setBusy(false);
    if (error) { setMsg('Error: ' + error.message); return; }
    setMsg('Invoice voided.'); loadInvoices();
  }
  const [planForm, setPlanForm] = useState({ tier_key: '', seats: '', status: 'active' });
  function loadPlan() {
    staffGetTenantPlan(org.id).then(({ data }) => {
      if (data) {
        setPlan(data);
        setPlanForm({
          tier_key: data.tier_key || (data.tiers && data.tiers[0] && data.tiers[0].tier_key) || '',
          seats: data.seats ?? '', status: data.status || 'active',
        });
      }
    });
  }
  async function savePlan() {
    if (!planForm.tier_key) { setMsg('Pick a tier.'); return; }
    setBusy(true); setMsg('');
    const { data, error } = await staffSetTenantPlan(org.id, planForm.tier_key, planForm.seats, planForm.status);
    setBusy(false);
    if (error) { setMsg('Error: ' + error.message); return; }
    setPlan(data); setMsg('Plan saved.'); onChanged?.();
  }
  async function clearPlan() {
    setBusy(true); setMsg('');
    const { data, error } = await staffClearTenantPlan(org.id);
    setBusy(false);
    if (error) { setMsg('Error: ' + error.message); return; }
    setPlan(data); setPlanForm({ ...planForm, tier_key: data.resolved_tier });
    setMsg('Plan cleared - tenant now defaults to Enterprise (fail-open).'); onChanged?.();
  }

  useEffect(() => {
    staffGetBilling(org.id).then(({ data }) => {
      if (data) setB({
        tier: data.tier || 'starter',
        monthly_min: data.monthly_min ?? '', rate_per_tau: data.rate_per_tau ?? '',
        token_allowance: data.token_allowance ?? '', overage_rate: data.overage_rate ?? '',
        contract_end: data.contract_end || '', billing_email: data.billing_email || '',
      });
    });
  }, [org.id]);

  function loadMembers() { staffListMembers(org.id).then(({ data }) => setMembers(data)); }
  useEffect(() => { if (tab === 'members') loadMembers(); }, [tab, org.id]);

  function loadInvites() { staffListInvitations(org.id).then(({ data }) => setInvites(data)); }
  useEffect(() => { if (tab === 'invites') loadInvites(); }, [tab, org.id]);
  useEffect(() => { if (tab === 'plan') loadPlan(); }, [tab, org.id]);
  useEffect(() => { if (tab === 'billing') loadInvoices(); }, [tab, org.id]);

  async function copyInvite(tok) {
    const url = `${CONSOLE_URL}/accept-invite?token=${tok}`;
    try { await navigator.clipboard.writeText(url); setCopiedTok(tok); setTimeout(() => setCopiedTok(null), 1500); }
    catch (e) { /* clipboard blocked */ }
  }
  async function resend(id) {
    setSendingId(id); setMsg('');
    const { error } = await sendInviteEmail(id);
    setSendingId(null);
    if (error) { setMsg('Error: ' + (error.message || 'Email send failed')); return; }
    setResentId(id); setTimeout(() => setResentId(null), 2500);
  }

  async function changeRole(uid, role) {
    setBusy(true);
    const { error } = await staffSetMemberRole(org.id, uid, role);
    setBusy(false);
    if (error) { setMsg('Error: ' + error.message); return; }
    loadMembers(); onChanged?.();
  }
  async function removeMember(uid) {
    setBusy(true);
    const { error } = await staffRemoveMember(org.id, uid);
    setBusy(false);
    if (error) { setMsg('Error: ' + error.message); return; }
    loadMembers(); onChanged?.();
  }

  async function saveDetails() {
    setBusy(true); setMsg('');
    const { error } = await staffUpdateOrg(org.id, d);
    setBusy(false);
    if (error) { setMsg('Error: ' + error.message); return; }
    setMsg('Saved.'); onChanged?.();
  }
  async function saveBilling() {
    setBusy(true); setMsg('');
    const { error } = await staffSetBilling(org.id, b);
    setBusy(false);
    if (error) { setMsg('Error: ' + error.message); return; }
    setMsg('Billing saved.'); onChanged?.();
  }
  async function toggleStatus() {
    const next = org.status === 'suspended' ? 'active' : 'suspended';
    setBusy(true);
    const { error } = await staffSetOrgStatus(org.id, next);
    setBusy(false);
    if (error) { setMsg('Error: ' + error.message); return; }
    setMsg(next === 'suspended' ? 'Org suspended.' : 'Org reactivated.'); onChanged?.();
  }
  async function doDelete() {
    setBusy(true);
    const { error } = await staffDeleteOrg(org.id);
    setBusy(false);
    if (error) { setMsg('Error: ' + error.message); return; }
    onChanged?.(); onClose();
  }

  const L = (label, val, set, ph, type = 'text') => (
    <div style={{ marginBottom: 12 }}>
      <label className="field-l">{label}</label>
      <input className="inp" type={type} value={val} placeholder={ph} onChange={(e) => set(e.target.value)} />
    </div>
  );

  return (
    <div className="wiz-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="wiz-card" style={{ maxWidth: 640, width: '92vw' }}>
        <button className="wiz-x" onClick={onClose}><X size={18} /></button>
        <h2 className="wiz-h" style={{ textAlign: 'left', fontSize: 19, marginBottom: 2 }}>Manage organization</h2>
        <div className="mono" style={{ fontSize: 12, color: 'var(--g-text-2)', marginBottom: 14 }}>{org.name} &middot; {org.id}</div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--g-divider)', marginBottom: 16, flexWrap: 'wrap' }}>
          {[['details', 'Details', Building2], ['billing', 'Billing & pricing', CreditCard], ['members', 'Members', Users], ['invites', 'Invites', Mail], ['plan', 'Plan & features', Award]].map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, border: 0, background: 'transparent', cursor: 'pointer',
                padding: '8px 12px', fontSize: 13, fontWeight: 500, borderBottom: tab === k ? '2px solid var(--g-admin, #b06000)' : '2px solid transparent',
                color: tab === k ? 'var(--g-text)' : 'var(--g-text-2)', marginBottom: -1 }}>
              <Icon size={15} />{label}
            </button>
          ))}
        </div>

        {tab === 'details' && (
          <>
            {L('Organization name', d.name, (v) => setD({ ...d, name: v }), 'Acme Inc.')}
            <div className="row r2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {L('Industry', d.industry, (v) => setD({ ...d, industry: v }), 'Fintech')}
              <div style={{ marginBottom: 12 }}>
                <label className="field-l">Size</label>
                <select className="inp" value={d.size} onChange={(e) => setD({ ...d, size: e.target.value })}>
                  {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="row r2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ marginBottom: 12 }}>
                <label className="field-l">Plan</label>
                <select className="inp" value={d.plan} onChange={(e) => setD({ ...d, plan: e.target.value })}>
                  {['trial', 'starter', 'business', 'enterprise'].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              {L('Primary color', d.primary_color, (v) => setD({ ...d, primary_color: v }), '#1a73e8')}
            </div>
            {L('Slug', d.slug, (v) => setD({ ...d, slug: v }), 'acme')}
            <button className="btn admin-primary" onClick={saveDetails} disabled={busy} style={{ marginTop: 4 }}>Save details</button>
          </>
        )}

        {tab === 'billing' && (
          <>
            <div className="banner admin" style={{ marginBottom: 14 }}>
              Seat-based invoice = seats x plan tier price. Govern360 issues it; the customer pays by Stripe or check; you mark it paid. No auto-charge. Set tier/seats under Plan &amp; features.
            </div>
            <div className="row r2" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'end', marginBottom: 16 }}>
              <div>
                <label className="field-l">Billing period (month)</label>
                <input className="inp" type="date" value={invPeriod} onChange={(e) => setInvPeriod(e.target.value)} />
              </div>
              <button className="btn admin-primary" onClick={genInvoice} disabled={busy}><FileText size={15} />Generate invoice</button>
            </div>
            {invoices.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--g-text-2)' }}>No invoices yet. Generate one for the selected period.</p>
            ) : (
              <table className="tb">
                <thead><tr><th>Invoice</th><th>Period</th><th>Seats</th><th>Total</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id}>
                      <td className="mono" style={{ fontSize: 12 }}>{inv.invoice_number}</td>
                      <td style={{ fontSize: 12, color: 'var(--g-text-2)' }}>{inv.period_start}</td>
                      <td>{inv.seats}</td>
                      <td className="mono">${Number(inv.total_amount).toFixed(2)}</td>
                      <td><span className={`pill ${inv.status === 'paid' ? 'p-succ' : inv.status === 'void' ? 'p-gray' : 'p-warn'}`}>{inv.status}</span></td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button className="btn outline" style={{ height: 28, padding: '0 10px', fontSize: 11, marginRight: 4 }} onClick={() => openInvoiceDocument(inv, org)}><Download size={13} />PDF</button>
                        {inv.status !== 'paid' && inv.status !== 'void' && (
                          <button className="btn outline" style={{ height: 28, padding: '0 10px', fontSize: 11, marginRight: 4 }} onClick={() => markPaid(inv)}><Check size={13} />Mark paid</button>
                        )}
                        {inv.status !== 'void' && inv.status !== 'paid' && (
                          <button className="btn outline" style={{ height: 28, padding: '0 10px', fontSize: 11, color: 'var(--g-danger)', borderColor: 'var(--g-danger)' }} onClick={() => voidInvoice(inv)}>Void</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
        {tab === 'members' && (
          <>
            {members.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--g-text-2)' }}>No members, or loading...</p>
            ) : (
              <table className="tb">
                <thead><tr><th>User</th><th>Role</th><th>MFA</th><th></th></tr></thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.user_id}>
                      <td><strong>{m.full_name || m.email || m.user_id}</strong><div style={{ fontSize: 11, color: 'var(--g-text-2)' }}>{m.email}</div></td>
                      <td>
                        <select className="inp" style={{ height: 30, fontSize: 12, width: 'auto' }} value={m.role} disabled={busy}
                          onChange={(e) => changeRole(m.user_id, e.target.value)}>
                          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </td>
                      <td>{m.mfa_enabled ? 'Yes' : 'No'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="btn outline" style={{ height: 28, padding: '0 10px', fontSize: 11, color: 'var(--g-danger)', borderColor: 'var(--g-danger)' }}
                          onClick={() => removeMember(m.user_id)} disabled={busy}><UserMinus size={13} />Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {tab === 'invites' && (
          <>
            <p style={{ fontSize: 12, color: 'var(--g-text-2)', marginTop: 0, marginBottom: 12 }}>
              Pending invitations for this organization. Copy the link to share manually, or resend the email.
            </p>
            {invites.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--g-text-2)' }}>No pending invitations.</p>
            ) : (
              <table className="tb">
                <thead><tr><th>Email</th><th>Role</th><th>Expires</th><th></th></tr></thead>
                <tbody>
                  {invites.map((i) => (
                    <tr key={i.id}>
                      <td><Mail size={14} style={{ verticalAlign: -2, marginRight: 6, color: 'var(--g-text-2)' }} />{i.email}</td>
                      <td><span className="pill p-gray">{i.role}</span></td>
                      <td style={{ color: 'var(--g-text-2)', fontSize: 12 }}>{i.expires_at ? new Date(i.expires_at).toLocaleDateString() : '-'}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button className="btn outline" style={{ height: 28, padding: '0 10px', fontSize: 11, marginRight: 4 }} onClick={() => copyInvite(i.token)}>
                          {copiedTok === i.token ? <><Check size={14} />Copied</> : <><Copy size={14} />Copy link</>}
                        </button>
                        <button className="btn outline" style={{ height: 28, padding: '0 10px', fontSize: 11 }} onClick={() => resend(i.id)} disabled={sendingId === i.id}>
                          {resentId === i.id ? <><Check size={14} />Sent!</> : sendingId === i.id ? 'Sending...' : <><Send size={14} />Resend</>}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {tab === 'plan' && (
          <>
            <div className="banner admin" style={{ marginBottom: 14 }}>
              {plan?.dormant
                ? 'This tenant has no assigned plan - it defaults to Enterprise (fail-open). Assign a tier to enforce feature access.'
                : `Assigned tier: ${plan?.tier_key || '-'} - features below reflect this tier.`}
            </div>
            <div className="row r2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ marginBottom: 12 }}>
                <label className="field-l">Plan tier (features)</label>
                <select className="inp" value={planForm.tier_key} onChange={(e) => setPlanForm({ ...planForm, tier_key: e.target.value })}>
                  <option value="">Select tier...</option>
                  {(plan?.tiers || []).map((t) => <option key={t.tier_key} value={t.tier_key}>{t.display_name} (${t.seat_price_usd}/seat)</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label className="field-l">Status</label>
                <select className="inp" value={planForm.status} onChange={(e) => setPlanForm({ ...planForm, status: e.target.value })}>
                  {['active', 'trial', 'suspended'].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            {L('Seats', planForm.seats, (v) => setPlanForm({ ...planForm, seats: v }), 'e.g. 25', 'number')}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button className="btn admin-primary" onClick={savePlan} disabled={busy}>Save plan</button>
              {!plan?.dormant && <button className="btn outline" onClick={clearPlan} disabled={busy}>Clear (fail-open)</button>}
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--g-text-3)', textTransform: 'uppercase', letterSpacing: '.05em', margin: '8px 0 8px' }}>Features in this tier</div>
            <table className="tb">
              <thead><tr><th>Feature</th><th style={{ textAlign: 'right' }}>Included</th></tr></thead>
              <tbody>
                {(plan?.features || []).map((f) => (
                  <tr key={f.feature_key}>
                    <td>{f.display_name}{f.sales_gated ? <span className="pill p-gray" style={{ marginLeft: 6, fontSize: 10 }}>sales</span> : null}</td>
                    <td style={{ textAlign: 'right' }}><span className={`pill ${f.included ? 'p-succ' : 'p-gray'}`}>{f.included ? 'Yes' : '-'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        {/* Danger zone */}
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--g-divider)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--g-text-3)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 10 }}>Lifecycle</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn outline" onClick={toggleStatus} disabled={busy}>
              {org.status === 'suspended' ? <><Play size={15} />Reactivate</> : <><Pause size={15} />Suspend</>}
            </button>
            {!confirmDel ? (
              <button className="btn outline" style={{ color: 'var(--g-danger)', borderColor: 'var(--g-danger)' }} onClick={() => setConfirmDel(true)} disabled={busy}>
                <Trash2 size={15} />Delete org
              </button>
            ) : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, color: 'var(--g-danger)', display: 'flex', alignItems: 'center', gap: 4 }}><AlertTriangle size={14} />Delete {org.name} and ALL its data?</span>
                <button className="btn" style={{ background: 'var(--g-danger)', color: '#fff' }} onClick={doDelete} disabled={busy}>Yes, delete</button>
                <button className="btn outline" onClick={() => setConfirmDel(false)} disabled={busy}>Cancel</button>
              </span>
            )}
          </div>
        </div>

        {msg && <div className="banner admin" style={{ marginTop: 14, marginBottom: 0 }}>{msg}</div>}
      </div>
    </div>
  );
}